import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaItem } from '@/types/posts';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { MediaPreview } from './MediaPreview';
import {
  validateFiles,
  MAX_FILES,
  MAX_FILE_SIZE,
  formatFileSize,
} from '@/utils/mediaValidation';
import { cn } from '@/utils/cn';
import { formatApiError } from '@/services/api';
import { toast } from '@/utils/toast';
import { recordSlowOp } from '@/utils/perfTelemetry';

/**
 * MediaUploadZone Component
 *
 * Drag-and-drop file upload zone with progress tracking
 */

export interface MediaUploadZoneProps {
  onUploadComplete?: (media: MediaItem[]) => void;
  onUploadError?: (error: Error) => void;
  maxFiles?: number;
  maxFileSize?: number;
  className?: string;
  /**
   * Whether to accept video files (default: true)
   */
  acceptVideo?: boolean;
  /**
   * Whether to accept PDF files (default: false)
   */
  acceptPdf?: boolean;
}

export const MediaUploadZone = ({
  onUploadComplete,
  onUploadError,
  maxFiles = MAX_FILES,
  maxFileSize = MAX_FILE_SIZE,
  className,
  acceptVideo = true,
  acceptPdf = false,
}: MediaUploadZoneProps) => {
  const { t } = useTranslation('common');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, removeUpload, uploads, reset } = useMediaUpload();

  /**
   * Handle file selection (with async magic bytes validation)
   */
  const handleFiles = async (files: File[]) => {
    setError(null);

    // Check if adding these files would exceed max
    const currentCount = uploads.length;
    const totalCount = currentCount + files.length;

    if (totalCount > maxFiles) {
      const errorMsg = t('too_many_files', { max: maxFiles, count: currentCount });
      setError(errorMsg);
      onUploadError?.(new Error(errorMsg));
      return;
    }

    // Validate files (async - includes magic bytes validation)
    const validateT0 = performance.now();
    const validation = await validateFiles(files);
    recordSlowOp('mediaValidateBatch', performance.now() - validateT0);
    if (!validation.valid) {
      setError(validation.error || t('invalid_files'));
      onUploadError?.(new Error(validation.error || t('invalid_files')));
      return;
    }

    // Upload all files
    try {
      // Get media items directly from upload results (avoids React state timing issues)
      const uploadT0 = performance.now();
      const uploadResults = await Promise.all(files.map((file) => uploadFile(file)));
      recordSlowOp('mediaUploadBatch', performance.now() - uploadT0);
      const completedMedia = uploadResults.map((result) => result.media);

      // Non-blocking storage-quota nudge (warn mode only) — fire once per
      // batch, not per file, even if multiple uploads in this batch are over.
      if (uploadResults.some((result) => result.storageWarning)) {
        toast.warning(t('storage_over_limit_toast'));
      }

      // Notify parent of completed uploads
      onUploadComplete?.(completedMedia);

      // Clear internal state - parent now owns the media
      reset();
    } catch (err) {
      const errorMsg = formatApiError(err).message;
      setError(errorMsg);
      onUploadError?.(err instanceof Error ? err : new Error(errorMsg));
    }
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    handleFiles(files);

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Open file picker
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const hasUploads = uploads.length > 0;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Upload Zone */}
      {!hasUploads && (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFilePicker}
          className={cn(
            'relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200',
            isDragging
              ? 'border-primary-500 bg-primary-50 scale-[1.01] shadow-md'
              : 'border-gray-300 hover:border-soft-mint hover:bg-off-white'
          )}
        >
          {/* Upload Icon */}
          <svg
            className={cn(
              'mx-auto h-12 w-12 transition-all duration-200',
              isDragging ? 'text-primary-500 scale-110' : 'text-gray-400'
            )}
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* Instructions */}
          <div className="mt-4 space-y-2">
            <div className="text-sm text-gray-600">
              <button
                type="button"
                className="font-medium text-dark-teal hover:text-bs-black focus:outline-none focus:underline"
              >
                {t('upload_files')}
              </button>
              <span className="ml-1">{t('or_drag_and_drop')}</span>
            </div>
            <p className="text-xs text-gray-500">
              {acceptVideo && acceptPdf
                ? t('file_types_images_videos_pdfs')
                : acceptVideo
                ? t('file_types_images_videos')
                : acceptPdf
                ? t('file_types_images_pdfs')
                : t('file_types_images')}
            </p>
            <p className="text-xs text-gray-500">
              {t('file_limits', { maxFiles, maxSize: formatFileSize(maxFileSize) })}
            </p>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={
              acceptVideo && acceptPdf
                ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,video/mp4,video/webm,video/quicktime,video/x-msvideo,application/pdf'
                : acceptVideo
                ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,video/mp4,video/webm,video/quicktime,video/x-msvideo'
                : acceptPdf
                ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,application/pdf'
                : 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp'
            }
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* Compact Upload Button (when files exist) */}
      {hasUploads && uploads.length < maxFiles && (
        <button
          type="button"
          onClick={openFilePicker}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 hover:border-primary-400 rounded-lg text-sm text-gray-600 hover:text-primary-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {t('add_more_files', { current: uploads.length, max: maxFiles })}
        </button>
      )}

      {/* Error Message - only show if parent doesn't handle errors */}
      {error && !onUploadError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Media Previews */}
      {hasUploads && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {uploads.map((upload) => (
            <MediaPreview
              key={upload.id}
              media={upload}
              onRemove={() => removeUpload(upload.id)}
            />
          ))}
        </div>
      )}

      {/* Max Files Message */}
      {uploads.length >= maxFiles && (
        <div className="p-3 bg-soft-mint border border-light-teal-gray rounded-lg">
          <p className="text-sm text-dark-teal">
            {t('max_files_reached', { max: maxFiles })}
          </p>
        </div>
      )}

      {/* Hidden File Input (for compact button) */}
      {hasUploads && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={
            acceptVideo && acceptPdf
              ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,video/mp4,video/webm,video/quicktime,video/x-msvideo,application/pdf'
              : acceptVideo
              ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,video/mp4,video/webm,video/quicktime,video/x-msvideo'
              : acceptPdf
              ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp,application/pdf'
              : 'image/jpeg,image/jpg,image/png,image/webp,image/gif,image/bmp'
          }
          onChange={handleFileInputChange}
          className="hidden"
        />
      )}
    </div>
  );
};
